import { environment } from "../../environments/environment";
import {
  Injectable,
  computed,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import {
  Database,
  ref,
  push,
  objectVal,
  listVal,
  query,
  orderByChild,
  equalTo,
  get,
  set,
  update,
  remove,
} from "@angular/fire/database";
import { of, concat, firstValueFrom, interval, combineLatest } from "rxjs";
import { switchMap, filter, take, timeout, map } from "rxjs/operators";
import { AuthService } from "../providers/auth.service";
import { ClubService } from "../providers/club.service";
import { FunctionsService } from "../providers/functions.service";
import {
  SiteRole,
  SiteCapability,
  SITE_ROLE_CAPS,
  ADMIN_SITE_CAPS,
  SITE_ROLE_LABEL_KEY,
  siteRoleHasCapability,
  ClubRole,
} from "../providers/permissions";
import { ConfigService } from "../providers/config.service";

export type ClubsitePrivacyMode = "full" | "short" | "anonymous";

export interface ClubsiteFeedConfig {
  enabled: boolean;
  // Stamped on every save; the server writes meta/projectedAt when the
  // rebuild it triggers completes. projectedAt < updatedAt = still projecting.
  updatedAt?: number;
  privacyMode: ClubsitePrivacyMode;
  // false keeps all money amounts (payouts, future buy-ins) out of the
  // public projections entirely. Default true.
  showMoney?: boolean;
  // false keeps every venue (address, map link) off the public site.
  showLocations?: boolean;
  // Player avatars on the public site: real photos or initials circles.
  // Sits with the name-display choice; winner photos are unaffected.
  avatarStyle?: "photos" | "initials";
  // Publication-consent attestation: the owner confirmed (once, on first
  // enable) that the club has its members' permission to publish names and
  // photos. Preserved across later config saves.
  acceptedBy?: string;
  acceptedAt?: number;
}

// TipTap document (JSON). Rendered server-side by the Astro renderer via
// @tiptap/html with a fixed extension whitelist (StarterKit + Link) — the
// whitelist doubles as sanitization.
export type RichTextDoc = { type: "doc"; content?: any[] };

// ---------------------------------------------------------------------------
// v3 builder model: a site is site-wide parameters + a keyed map of PAGES;
// each page is an ordered list of typed, individually-configured SECTIONS.
// ---------------------------------------------------------------------------

export type SiteAppearance = "light" | "dark";

// The thin data strip at the very top of every page ("scoreboard ribbon").
export interface SiteTicker {
  enabled: boolean;
  items: {
    nextEvent?: boolean;
    lastResult?: boolean;
    leader?: boolean;
  };
}

// Site chrome: header and footer are their own config sections, each with
// style options — the pages are the site's content between them.
export type HeaderStyle = "bar" | "centered" | "minimal";
// Desktop header layouts, any theme (phones always get the menu button).
// KEEP IN SYNC with the renderer's lib/sites.ts HEADER_LAYOUTS.
export type HeaderLayout = "centered" | "inline" | "columns" | "burger";
export const HEADER_LAYOUTS: { value: HeaderLayout; labelKey: string }[] = [
  { value: "centered", labelKey: "WEBSITE_HEADER_STYLE_CENTERED" },
  { value: "inline", labelKey: "WEBSITE_HEADER_STYLE_SIDE" },
  { value: "columns", labelKey: "WEBSITE_HEADER_STYLE_COLUMNS" },
  { value: "burger", labelKey: "WEBSITE_HEADER_STYLE_BURGER" },
];
export interface SiteHeaderConfig {
  // The stored style only maps old "minimal" headers to a hidden logo.
  style: HeaderStyle;
  // Absent = the theme's own (SiteThemeDef.headerLayout).
  layout?: HeaderLayout;
  showLogo?: boolean;
  showSocial?: boolean;
  showMemberLogin?: boolean;
  // "Join the club" button linking to the club link (/join/{clubID});
  // "Join a club" → the Clubs page on multi-club sites. Absent = shown.
  showJoin?: boolean;
}
export const DEFAULT_HEADER: SiteHeaderConfig = {
  style: "bar",
  showSocial: true,
  showMemberLogin: true,
  showJoin: true,
};

export type FooterStyle = "full" | "minimal";
export interface SiteFooterConfig {
  style: FooterStyle;
  showSocial?: boolean;
  showMemberLogin?: boolean;
  showJoin?: boolean;
  showTagline?: boolean;
  // Full footer: the club's home location address (from Club Settings).
  showAddress?: boolean;
}
export const DEFAULT_FOOTER: SiteFooterConfig = {
  style: "full",
  showSocial: true,
  showMemberLogin: true,
  showJoin: true,
  showTagline: true,
  showAddress: true,
};

export type SiteSectionType =
  | "hero"
  | "content"
  | "results"
  | "standings"
  | "upcoming"
  | "stats"
  | "clubs"
  | "locations"
  | "club-header"
  | "files"
  | "embed"
  | "gallery"
  | "articles-latest"
  | "articles-all"
  | "albums";

export interface SiteFile {
  name: string;
  url: string;
  size?: number;
  mime?: string;
  order: number;
}
export interface SitePhoto {
  url: string;
  // Album photos: a 640 px copy for grids (the full photo is for the
  // lightbox). Missing on photos uploaded before 2026-10.
  thumbUrl?: string | null;
  caption?: string | null;
  order: number;
}

export type HeroStyle = "watermark" | "typographic" | "photo";

// One pragmatic interface rather than a discriminated union: RTDB stores the
// superset, template-driven forms bind optionals per type. Which fields apply
// to which type:
//   hero:            style, photoURL (style "photo"), showTagline, showJoin, showLogin
//   content:         title?, body
//   results:         title?, count (0 = all), clubs?
//   standings:       title?, scope ("active" | explicit league map), rows?, podium?, clubs?
//   upcoming:        title?, count, clubs?
//   stats:           title?, show {members, tournaments, lastField, leaderPoints, clubs}, clubs?
//   clubs:           title?, clubs? (card per club, links to the club pages)
//   locations:       title?, clubs? (card per venue from Club Settings locations)
//   club-header:     clubs (one club: big logo, name, home venue address, map link)
//   content (image): imageURL?, imageAlt?, imagePosition? (left|right|top)
//   files:           title?, files {id: {name,url,size,mime,order}}
//   embed:           title?, url (YouTube/Vimeo/Google Maps embed), caption?
//   gallery:         albumId (title? overrides the album title), count (photos
//                    shown, then a "See all" card; absent = 3, 0 = all); legacy
//                    sections carry photos {id: {url,caption,order}} inline
//                    until the editor converts them into an album
//   albums:          title? (card per listed album, links to /albums/{id})
//   articles-latest: title?, count
//   articles-all:    title?
// clubs? = club filter on multi-club sites; absent/null = every site club.
export interface SiteSection {
  type: SiteSectionType;
  order: number;
  title?: string | null;
  style?: HeroStyle;
  photoURL?: string | null;
  // hero: badge = small text above the headline (else the SITES_KICKER
  // wording); headline = title (falls back to the tagline, then the name);
  // highlight = words of it the theme paints; description = paragraph
  badge?: string | null;
  highlight?: string | null;
  description?: string | null;
  showTagline?: boolean;
  // hero: "Join the club" / "Member login" buttons under the tagline
  // (absent = hidden).
  showJoin?: boolean;
  showLogin?: boolean;
  body?: RichTextDoc;
  count?: number;
  clubs?: Record<string, true> | null;
  scope?: "active" | Record<string, true>;
  rows?: number;
  podium?: boolean;
  show?: {
    members?: boolean;
    tournaments?: boolean;
    lastField?: boolean;
    leaderPoints?: boolean;
    clubs?: boolean; // multi-club sites only: club count
  };
  imageURL?: string | null;
  imageAlt?: string | null;
  imagePosition?: "left" | "right" | "top";
  url?: string | null;
  caption?: string | null;
  files?: Record<string, SiteFile> | null;
  photos?: Record<string, SitePhoto> | null;
  albumId?: string | null;
}

export interface SitePage {
  title: string; // menu/nav label; "" on home = renderer's localized "Home"
  slug: string; // URL path segment; "" = the home page (exactly one, undeletable)
  order: number; // menu + list order
  inMenu: boolean;
  clubId?: string; // builder-managed per-club page on multi-club sites
  sections: Record<string, SiteSection>;
}

export interface SiteArticle {
  id?: string;
  title: string;
  body: RichTextDoc;
  date: number;
  published: boolean;
  authorName?: string;
  // Stamped on create; a Contributor may only edit articles carrying their
  // own uid (rules). Legacy articles without it are Editor+ only.
  authorUid?: string;
  photoURL?: string | null;
  albumId?: string | null; // photo album shown below the body
}

// A site-level photo album: like articles, saved live (not drafted) and
// writable by Staff. Shown by gallery sections (albumId), posts (albumId),
// the albums section (cards of listed albums) and /albums/{id}.
// Photos live in Storage at {club}/sitePhotos/album-{albumId}-{photoId}.
export interface SiteAlbum {
  id?: string;
  title: string;
  date: number;
  listed: boolean; // shown on the albums section (Photos page)
  photos?: Record<string, SitePhoto> | null; // cover = first by order
  createdAt: number;
}

export type SiteAlbumEntry = SiteAlbum & { id: string; cover: string | null; count: number };

// Albums of a site, newest first, with their cover (first photo) and count.
export function albumList(site: { albums?: Record<string, SiteAlbum> } | null | undefined): SiteAlbumEntry[] {
  return Object.entries(site?.albums || {})
    .map(([id, a]) => {
      const photos = Object.values(a.photos || {}).sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
      return { ...a, id, cover: photos[0]?.url || null, count: photos.length };
    })
    .sort((a, b) => b.date - a.date);
}

// Site team entry (clubsites/{id}/team/{uid}); written by the
// setClubsiteTeamRole callable only. memberName is a display snapshot so the
// Team card can show members of linked clubs the viewer can't read.
export interface SiteTeamEntry {
  role: SiteRole;
  addedAt: number;
  addedBy: string;
  memberName?: string;
  clubId?: string; // linked club whose membership was verified
}
export interface SiteTeamRow extends SiteTeamEntry {
  uid: string;
}

export interface ClubsiteBilling {
  status: string;
  stripeSubId: string;
  uid: string;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  comp?: boolean; // pilot/comped sites — set server-side, never by the client
}

// Order is the render order on the public site. Facebook first: many clubs
// run their whole discussion in a Facebook group (a group URL works the same
// as a page URL here).
export const SOCIAL_NETWORKS = [
  "facebook",
  "instagram",
  "youtube",
  "x",
  "whatsapp",
  "discord",
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

// Public-site chrome strings a site may override with its own wording
// (site.textOverrides). MUST mirror the EN table in the renderer:
// blindvalet-sites/src/lib/i18n.ts. Shown to the owner via the normal
// translate pipe (same keys exist in /translations).
// (SITES_KICKER left this list: it's the hero's own Badge field now; an old
// override moves into empty hero badges on load.)
export const SITE_TEXT_KEYS = [
  "SITES_NAV_HOME",
  "SITES_NAV_RESULTS",
  "SITES_NAV_STANDINGS",
  "SITES_CLUBS_TITLE",
  "SITES_VIEW_CLUB",
  "SITES_MEMBERS",
  "SITES_LOCATIONS_TITLE",
  "SITES_OPEN_MAP",
  "SITES_HOME_VENUE",
  "SITES_DOWNLOAD",
  "SITES_NOT_FOUND_TITLE",
  "SITES_NOT_FOUND_BODY",
  "SITES_BACK_HOME",
  "SITES_GALLERY_CLOSE",
  "SITES_MEMBER_LOGIN",
  "SITES_JOIN_CLUB",
  "SITES_JOIN_A_CLUB",
  "SITES_JOIN",
  "SITES_ABOUT_TITLE",
  "SITES_JOIN_TITLE",
  "SITES_RESULTS_TITLE",
  "SITES_STANDINGS_TITLE",
  "SITES_LATEST_RESULTS",
  "SITES_ALL_RESULTS",
  "SITES_UPCOMING_TITLE",
  "SITES_ARTICLES_TITLE",
  "SITES_STAT_MEMBERS",
  "SITES_STAT_TOURNAMENTS",
  "SITES_STAT_LAST_FIELD",
  "SITES_STAT_LEADER_POINTS",
  "SITES_WINNER",
  "SITES_LEADER",
  "SITES_NEXT",
  "SITES_LAST",
  "RUNNING", // the app's status word, reused by the site's live marker
  "SITES_PLAYER",
  "SITES_PLAYERS",
  "SITES_PLAYERS_LEFT",
  "SITES_POINTS",
  "SITES_KOS",
  "SITES_PAYOUT",
  "SITES_TOURNAMENTS",
  "SITES_RANK",
  "SITES_BACK",
  "SITES_MENU",
  "SITES_LOAD_MORE",
  "SITES_SEASON_FINISHED",
  "SITES_EMPTY_SEASON",
  "SITES_NO_RESULTS",
  "SITES_PREPARING",
  "SITES_NO_UPCOMING",
  "SITES_CREDIT",
  "SITES_LATEST_TOURNAMENT",
  "SITES_FINISH_ORDER",
  "SITES_SEE_ALL_RESULTS",
  "SITES_NO_ARTICLES",
  "SITES_ANONYMOUS_PLAYER",
  "SITES_SIGN_OUT",
  "SITES_YOU",
  "SITES_REGISTERED",
  "SITES_REGISTER",
  "SITES_UNREGISTER",
  "SITES_ACTION_FAILED",
  "SITES_OPEN_IN_APP",
  "SITES_JOIN_TO_REGISTER",
  "SITES_REQUEST_SENT",
  "SITES_LEVEL",
  "SITES_ANTE",
  "SITES_ON_BREAK",
  "SITES_LEFT_OF",
  "SITES_PRIZE_POOL",
  "SITES_PLACES_PAID",
  "SITES_YOU_IN",
  "SITES_YOU_OUT",
] as const;

export interface ClubSiteEntity {
  id?: string;
  ownerUid: string;
  clubIds: Record<string, true>;
  textOverrides?: Record<string, string> | null;
  primaryClubId: string; // creating club; articles rules check its Staff role
  slug: string;
  customDomain?: string;
  language: string;
  published: boolean;
  name: string;
  // null (not just absent) on the optionals below: updateSite() merges with
  // RTDB update(), so clearing a previously-set value requires an explicit
  // null to delete the key.
  tagline?: string | null;
  social?: Partial<Record<SocialNetwork, string>> | null; // profile/group URLs
  theme: {
    // Theme + palette (site-themes.ts); colors = sparse owner overrides on
    // top of the palette (null clears a key on the RTDB merge).
    id?: string;
    palette?: string;
    colors?: Record<string, string | null> | null;
    // Single-ink logo, so any logo sits on any palette.
    logoTreatment?: "original" | "mono";
    // Pre-themes fields: read only to map legacy sites (legacyThemeChoice).
    primary?: string;
    accent?: string;
    appearance?: SiteAppearance;
    fontPair?: string;
    avatarStyle?: "photos" | "initials"; // legacy location — now on the feed config
    logoURL?: string;
    logoSource?: "club" | "custom";
  };
  ticker: SiteTicker;
  header?: SiteHeaderConfig;
  footer?: SiteFooterConfig;
  pages: Record<string, SitePage>;
  articles?: Record<string, SiteArticle>;
  albums?: Record<string, SiteAlbum>;
  billing?: ClubsiteBilling; // server-written only (rules reject client writes)
  // Website roles (Webmaster / Editor / Contributor) — server-written only,
  // via the setClubsiteTeamRole callable. The owner is implicit (ownerUid).
  team?: Record<string, SiteTeamEntry>;
  // Bring-your-own-domain request (owner-written; status verified by the
  // checkClubsiteDomain callable; activation is a Blind Valet step for now).
  customDomainRequest?: ClubsiteDomainRequest | null;
  // Provenance of the live design (written by publishDraft).
  live?: { publishedAt: number; fromDraftId: string; fromDraftName: string } | null;
  // Named design drafts; editing always happens in one of these and the
  // root design keys change only when a draft is published.
  drafts?: Record<string, SiteDraft>;
}

// The versioned part of a site: everything the editor can change. The root
// holds the LIVE values, each draft holds the same keys under
// drafts/{id}/design; only Publish copies them onto the root. KEEP IN SYNC
// with DRAFT_KEYS in blindvalet-sites/src/lib/sites.ts. Not drafted:
// ownerUid, primaryClubId, customDomain, published, billing, articles,
// albums.
export const DRAFT_KEYS = [
  "name",
  "tagline",
  "language",
  "social",
  "slug",
  "clubIds",
  "theme",
  "ticker",
  "header",
  "footer",
  "pages",
  "textOverrides",
] as const;
export const DESIGN_KEYS = DRAFT_KEYS;
// Partial: drafts saved before a key was versioned lack it and fall back to
// the live value when loaded (see draftOf / applyDraft).
export type SiteDesign = Partial<Pick<ClubSiteEntity, (typeof DRAFT_KEYS)[number]>>;

// Same key set (order-insensitive) — for the clubIds map.
function sameKeys(a: any, b: any): boolean {
  const ka = Object.keys(a || {}).sort();
  const kb = Object.keys(b || {}).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i]);
}

// Legacy v1/v2 keys must not linger under the site node, but a null write
// to a key with no rule is denied for team members — purge only when the
// key is actually there.
function purgeLegacyKeys(site: ClubSiteEntity, data: Record<string, any>) {
  for (const key of ["sections", "template", "content"]) {
    if ((site as any)[key] !== undefined) data[key] = null;
  }
}

export interface ClubsiteDomainRequest {
  domain: string; // as typed, normalized: "poker.myclub.com" or "myclub.com"
  apex: boolean; // true = bare domain (we also take www)
  requestedAt: number;
  status: "pending" | "verified" | "active";
  activatedAt?: number;
  shardSiteId?: string;
  lastCheck?: {
    at: number;
    ok: boolean;
    hosts: { host: string; type: "CNAME" | "A"; expected: string; found: string[]; ok: boolean }[];
  } | null;
}

// DNS targets shown in the domain instructions; overridable from RTDB
// /config/clubsites so the shard hostname can change without a release.
export const DEFAULT_DOMAIN_TARGETS = {
  cnameTarget: "sites.blindvalet.site",
  apexIP: "75.2.60.5",
  // Where "buy a domain" sends the owner (affiliate link once approved);
  // overridable from RTDB /config/clubsites/registrarUrl like the targets.
  registrarUrl: "https://www.name.com/",
};

// Hosted sites: <slug>.<SITES_BASE_DOMAIN> (blindvalet.site in prod, a dev
// wildcard in DEV). A separate domain from blindvalet.com on purpose: user
// content must not share the brand domain's cookies, SEO or reputation.
export const SITES_BASE_DOMAIN: string =
  (environment as any).sitesBaseDomain || "blindvalet.site";
// The site's public address. DEV has no hosted renderer (everything runs on
// the local blindvalet-sites dev server), so with a preview base configured
// the address is that server with the ?site= selection.
export function hostedSiteUrl(slug: string): string {
  const local = (environment as any).sitesPreviewBase;
  if (local) return `${local}/?site=${encodeURIComponent(slug)}`;
  return `https://${slug}.${SITES_BASE_DOMAIN}`;
}

// Normalize what the owner typed into a bare hostname; null when unusable.
export function normalizeCustomDomain(input: string): string | null {
  let d = String(input || "").trim().toLowerCase();
  d = d.replace(/^[a-z]+:\/\//, "").split("/")[0].split("?")[0].replace(/\.+$/, "");
  if (d.startsWith("www.")) d = d.slice(4);
  if (!/^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(d)) return null;
  // Our own domains are never "custom".
  for (const own of ["blindvalet.com", "blindvalet.site"]) {
    if (d === own || d.endsWith(`.${own}`)) return null;
  }
  return d;
}
// Apex = registrable domain with a single label before the TLD-ish tail
// (myclub.com, myclub.co.uk is treated as apex too via the 2-label rule).
export function isApexDomain(domain: string): boolean {
  const parts = domain.split(".");
  if (parts.length <= 2) return true;
  const secondLevel = new Set(["co", "com", "org", "net", "gov", "edu", "ac"]);
  return parts.length === 3 && secondLevel.has(parts[1]) && parts[2].length === 2;
}

export interface SiteDraft {
  name: string;
  createdAt: number;
  updatedAt: number;
  basedOn?: string; // "live" | draftId | "starter:<key>"
  auto?: boolean; // the single "Previous live" backup written on publish
  design: SiteDesign;
}
export interface SiteDraftRow extends SiteDraft {
  id: string;
}
export const PREVIOUS_LIVE_ID = "previous-live";

export function designOf(entity: Partial<ClubSiteEntity> | null | undefined): SiteDesign {
  const src: any = entity || {};
  const out: any = {};
  for (const key of DRAFT_KEYS) out[key] = src[key] ?? null;
  return out as SiteDesign;
}

// A draft's full editable state: keys the draft carries win, the live site
// fills in the rest (drafts created before a key was versioned).
export function draftOf(site: ClubSiteEntity, draft: SiteDesign | null | undefined): SiteDesign {
  const out: any = designOf(site);
  const d: any = draft || {};
  for (const key of DRAFT_KEYS) if (key in d) out[key] = d[key];
  return out as SiteDesign;
}

// Page slugs the renderer owns: canonical detail routes (/articles/{id},
// /albums/{id}, /tournaments/{club}/{id}, /leagues/{club}/{id}) and the
// widget namespace.
export const RESERVED_PAGE_SLUGS = new Set([
  "articles",
  "albums",
  "embed",
  "tournaments",
  "leagues",
]);

export function wrapPlainText(text: string): RichTextDoc {
  const paragraphs = String(text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => ({
      type: "paragraph",
      content: [{ type: "text", text: line }],
    }));
  return { type: "doc", content: paragraphs };
}

// Legacy v2 template key → its appearance, which (with the old primary)
// picks the theme + palette through legacyThemeChoice (site-themes.ts).
const TEMPLATE_UPGRADE: Record<string, { appearance: SiteAppearance }> = {
  classic: { appearance: "light" },
  editorial: { appearance: "light" },
  felt: { appearance: "light" },
  midnight: { appearance: "dark" },
  arena: { appearance: "dark" },
};

export const DEFAULT_TICKER: SiteTicker = {
  enabled: true,
  items: { nextEvent: true, lastResult: true, leader: true },
};

// v1 stored sections as [{key, enabled}] + content strings; v2 as a keyed
// map of typed section-pages. Upgrade both to the v3 pages model in-memory
// on load; the next save persists v3. Preserves the v2 rendering: a home
// page composed of hero + stats + first content + latest results/articles,
// plus one page per remaining enabled v2 section.
export function upgradeSiteShape(site: any): ClubSiteEntity {
  if (!site) return site;
  if (site.pages) {
    if (!site.primaryClubId) {
      site.primaryClubId = Object.keys(site.clubIds || {})[0] || "";
    }
    return site as ClubSiteEntity;
  }

  // v1 → v2 sections map first, so one v2→v3 path serves both.
  let sections: Record<string, any> = site.sections || {};
  if (Array.isArray(site.sections)) {
    const old: Record<string, { enabled: boolean }> = {};
    for (const s of site.sections) old[s.key] = { enabled: !!s.enabled };
    sections = {
      about: {
        type: "content",
        title: "About the club",
        slug: "about",
        enabled: old["about"]?.enabled ?? true,
        order: 0,
        body: wrapPlainText(site.content?.about || ""),
      },
      join: {
        type: "content",
        title: "How to join",
        slug: "join",
        enabled: old["join"]?.enabled ?? true,
        order: 1,
        body: wrapPlainText(site.content?.join || ""),
      },
      results: {
        type: "results",
        title: "Tournament results",
        slug: "results",
        enabled: old["results"]?.enabled ?? true,
        order: 2,
      },
      standings: {
        type: "standings",
        title: "League standings",
        slug: "standings",
        enabled: old["league"]?.enabled ?? true,
        order: 3,
      },
    };
  }

  const ordered = Object.values(sections)
    .filter((s: any) => s && s.enabled)
    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  const pages: Record<string, SitePage> = {};
  let pageOrder = 0;

  // Home: hero + stats + first content body + latest results/articles —
  // the composition v2's index page rendered.
  const firstContent = ordered.find((s: any) => s.type === "content");
  const homeSections: Record<string, SiteSection> = {
    hero: { type: "hero", order: 0, style: "watermark", showTagline: true },
    stats: {
      type: "stats",
      order: 1,
      show: { members: true, tournaments: true, lastField: true },
    },
  };
  let homeOrder = 2;
  if (firstContent) {
    homeSections["intro"] = {
      type: "content",
      order: homeOrder++,
      title: firstContent.title || null,
      body: firstContent.body || { type: "doc", content: [] },
    };
  }
  if (ordered.some((s: any) => s.type === "results")) {
    homeSections["latest"] = { type: "results", order: homeOrder++, count: 3 };
  }
  if (ordered.some((s: any) => s.type === "articles")) {
    homeSections["news"] = {
      type: "articles-latest",
      order: homeOrder++,
      count: 3,
    };
  }
  pages["home"] = {
    title: "",
    slug: "",
    order: pageOrder++,
    inMenu: true,
    sections: homeSections,
  };

  for (const s of ordered) {
    if (s === firstContent) continue; // inlined on home
    let section: SiteSection | null = null;
    if (s.type === "content") {
      section = {
        type: "content",
        order: 0,
        title: s.title || null,
        body: s.body || { type: "doc", content: [] },
      };
    } else if (s.type === "results") {
      section = { type: "results", order: 0, count: 0 };
    } else if (s.type === "standings") {
      section = { type: "standings", order: 0, scope: "active", podium: true };
    } else if (s.type === "articles") {
      section = { type: "articles-all", order: 0 };
    }
    if (!section) continue;
    const slug = RESERVED_PAGE_SLUGS.has(s.slug) ? `${s.slug}-page` : s.slug;
    pages[s.slug || `page${pageOrder}`] = {
      title: s.title || s.slug,
      slug,
      order: pageOrder++,
      inMenu: true,
      sections: { main: section },
    };
  }

  const upgrade = TEMPLATE_UPGRADE[site.template] || TEMPLATE_UPGRADE.classic;
  const { content, sections: legacySections, template, ...rest } = site;
  return {
    ...rest,
    primaryClubId:
      site.primaryClubId || Object.keys(site.clubIds || {})[0] || "",
    theme: { ...(site.theme || {}), ...upgrade },
    ticker: site.ticker || { ...DEFAULT_TICKER },
    pages,
  } as ClubSiteEntity;
}

// Reserved labels + shape rules live in reserved-slugs.ts (identical copy in
// blindvalet-sites/src/lib; the renderer's test suite asserts they match).
import { isReservedSlug, isClaimableSlug } from "./reserved-slugs";
export { isReservedSlug, isClaimableSlug };

export function slugifySiteName(value: string): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

@Injectable({ providedIn: "root" })
export class ClubsiteService {
  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);
  private readonly auth = inject(AuthService);
  private readonly clubServ = inject(ClubService);
  private readonly fbFunc = inject(FunctionsService);
  private readonly configServ = inject(ConfigService);

  // A Blind Valet admin holding the active club under bvadmin's Take Control
  // (clubs/{id}/adminControl): edits its site like the owner, billing aside.
  // Release Control clears the flag, and with it the rights (database rules
  // and the clubsites.js callables check the same pair).
  readonly adminInControl = computed(() => {
    const uid = this.auth.uid;
    return !!uid && !!this.clubServ.club()?.adminControl && this.configServ.isAdmin(uid);
  });

  /**
   * May create the club's site and change its feed config (privacy, money,
   * locations under clubsitesPublic/{club}/config): the club owner or an
   * admin in control, as the database rules check. Not the site team.
   */
  readonly canClubConfig = computed(() => this.clubServ.currentRole() === ClubRole.Owner || this.adminInControl());

  /**
   * May open the builder: the club owner (who can create a site), anyone on
   * the active site's team, or an admin in control. What they may change
   * there comes from can().
   */
  readonly canEdit = computed(
    () => this.clubServ.currentRole() === ClubRole.Owner || !!this.currentSiteRole() || this.adminInControl()
  );

  // Sites owned by the signed-in user (re-keyed on club switch so the query
  // re-runs after login/club changes; requires .indexOn ownerUid).
  // undefined until the first emission ("not loaded" vs "none").
  readonly ownedSites = toSignal(
    toObservable(this.clubServ.selectedClubId).pipe(
      switchMap(() => {
        const uid = this.auth.uid;
        if (!uid) return of([] as ClubSiteEntity[]);
        return runInInjectionContext(this.injector, () =>
          listVal<ClubSiteEntity>(
            query(
              ref(this.mdb, "clubsites"),
              orderByChild("ownerUid"),
              equalTo(uid)
            ),
            { keyField: "id" }
          )
        );
      })
    )
  );

  // The active club's site when an admin is in control (anchored on
  // primaryClubId; requires .indexOn primaryClubId).
  readonly controlledSites = toSignal(
    toObservable(computed(() => (this.adminInControl() ? this.clubServ.selectedClubId() : null))).pipe(
      switchMap((clubID) =>
        clubID
          ? runInInjectionContext(this.injector, () =>
              listVal<ClubSiteEntity>(
                query(ref(this.mdb, "clubsites"), orderByChild("primaryClubId"), equalTo(clubID)),
                { keyField: "id" }
              )
            )
          : of([] as ClubSiteEntity[])
      )
    ),
    { initialValue: [] as ClubSiteEntity[] }
  );

  // Sites the user is on the team of. RTDB can't query a nested map, so the
  // setClubsiteTeamRole callable maintains profiles/{uid}/sites/{siteId} as a
  // discovery index; each site record is then streamed live (world-readable).
  // The role itself always comes from site.team (a forged index grants nothing).
  // Back to undefined ("not loaded") on every sign-in, so a hard reload that
  // first runs signed out can't pass an empty team list off as loaded (the
  // /website guard would then bounce a team member to the lobby).
  private readonly teamSiteIds = toSignal(
    toObservable(this.auth.uidSignal).pipe(
      switchMap((uid) =>
        uid
          ? concat(
              of(undefined),
              runInInjectionContext(this.injector, () =>
                listVal<{ id: string }>(ref(this.mdb, `profiles/${uid}/sites`), {
                  keyField: "id",
                })
              )
            )
          : of([] as { id: string }[])
      )
    )
  );
  private readonly teamSites = toSignal(
    toObservable(
      computed(() => {
        // listVal emits null (not []) for a missing node.
        const ids = this.teamSiteIds();
        return ids === undefined ? undefined : (ids || []).map((s) => s.id).join(",");
      })
    ).pipe(
      switchMap((joined) => {
        if (joined === undefined) return of(undefined);
        const ids = joined ? joined.split(",") : [];
        if (!ids.length) return of([] as ClubSiteEntity[]);
        return combineLatest(
          ids.map((id) =>
            runInInjectionContext(this.injector, () =>
              objectVal<ClubSiteEntity>(ref(this.mdb, `clubsites/${id}`))
            ).pipe(map((s) => (s ? ({ ...s, id } as ClubSiteEntity) : null)))
          )
        ).pipe(
          map((sites) => sites.filter((s): s is ClubSiteEntity => !!s))
        );
      })
    ),
    { initialValue: undefined as ClubSiteEntity[] | undefined }
  );

  // Every site the user can open in the editor: owned first, then team
  // sites, then the one an admin in control holds (deduped). undefined until
  // owned and team sites have emitted once.
  readonly mySites = computed<ClubSiteEntity[] | undefined>(() => {
    const owned = this.ownedSites();
    const team = this.teamSites();
    if (owned === undefined || team === undefined) return undefined;
    const seen = new Set<string>();
    const all: ClubSiteEntity[] = [];
    // listVal emits null (not []) for an empty query / missing node.
    for (const s of [...(owned || []), ...(team || []), ...(this.controlledSites() || [])]) {
      if (!s.id || seen.has(s.id)) continue;
      seen.add(s.id);
      all.push(s);
    }
    return all;
  });
  readonly sitesLoaded = computed(() => this.mySites() !== undefined);

  // The site for the active club: anchored on primaryClubId first (the club
  // it is configured from, which may itself be excluded from the site's
  // content), then by clubIds membership (a site can span several clubs).
  readonly site = computed<ClubSiteEntity | null>(() => {
    const clubID = this.clubServ.selectedClubId();
    if (!clubID) return null;
    const sites = this.mySites() || [];
    return (
      sites.find((s) => s.primaryClubId === clubID) ??
      sites.find((s) => s.clubIds && s.clubIds[clubID]) ??
      null
    );
  });

  // --- Website capability layer (see providers/permissions.ts) ---
  /**
   * Current user's role on the active site. ownerUid wins; otherwise the
   * team entry; anything else (including club roles) is no role at all.
   */
  readonly currentSiteRole = computed<SiteRole | null>(() => {
    const site = this.site();
    const uid = this.auth.uid;
    if (!site || !uid) return null;
    if (site.ownerUid === uid) return SiteRole.Owner;
    const r = site.team?.[uid]?.role as SiteRole | undefined;
    return r && SITE_ROLE_CAPS[r] ? r : null;
  });

  can(cap: SiteCapability): boolean {
    return (
      siteRoleHasCapability(this.currentSiteRole(), cap) ||
      (ADMIN_SITE_CAPS.has(cap) && this.adminInControl())
    );
  }

  get isSiteOwner(): boolean {
    return this.currentSiteRole() === SiteRole.Owner;
  }
  get canManageTeam(): boolean {
    return this.can(SiteCapability.MANAGE_TEAM);
  }
  get canDesign(): boolean {
    return this.can(SiteCapability.SITE_DESIGN);
  }
  get canSettings(): boolean {
    return this.can(SiteCapability.SITE_SETTINGS);
  }
  get canDomain(): boolean {
    return this.can(SiteCapability.SITE_DOMAIN);
  }
  get canOnline(): boolean {
    return this.can(SiteCapability.SITE_ONLINE);
  }
  get canPages(): boolean {
    return this.can(SiteCapability.SITE_PAGES);
  }
  get canPublish(): boolean {
    return this.can(SiteCapability.SITE_PUBLISH);
  }
  get canArticlesAll(): boolean {
    return this.can(SiteCapability.ARTICLES_ALL);
  }
  get canArticles(): boolean {
    return this.can(SiteCapability.ARTICLES_OWN);
  }
  /** Editors edit everything; Contributors only what they wrote. */
  canEditArticle(article: SiteArticle): boolean {
    if (this.canArticlesAll) return true;
    return (
      this.canArticles &&
      !!article.authorUid &&
      article.authorUid === this.auth.uid
    );
  }

  siteRoleLabelKey(role: string | null | undefined): string {
    return SITE_ROLE_LABEL_KEY[role as SiteRole] || "";
  }

  // Clubs whose feed the website admin card controls: every club on the
  // site once one exists (the site's own club may be excluded from the
  // content but still manages it), else just the active club.
  readonly feedClubIds = computed<string[]>(() => {
    const site = this.site();
    const ids = site ? Object.keys(site.clubIds || {}) : [];
    if (ids.length) return ids;
    const clubID = this.clubServ.selectedClubId();
    return clubID ? [clubID as string] : [];
  });

  // Feed opt-in per club (server projects results/standings only while a
  // club's config is enabled — see functions/clubsites.js).
  readonly feedConfigs = toSignal(
    toObservable(computed(() => this.feedClubIds().join(","))).pipe(
      switchMap((joined) => {
        const ids = joined ? joined.split(",") : [];
        if (!ids.length)
          return of({} as Record<string, ClubsiteFeedConfig | null>);
        return combineLatest(
          ids.map((id) =>
            runInInjectionContext(this.injector, () =>
              objectVal<ClubsiteFeedConfig>(
                ref(this.mdb, `clubsitesPublic/${id}/config`)
              )
            ).pipe(map((cfg) => [id, cfg ?? null] as const))
          )
        ).pipe(map((pairs) => Object.fromEntries(pairs)));
      })
    ),
    { initialValue: {} as Record<string, ClubsiteFeedConfig | null> }
  );

  // Representative config for the card (writes broadcast to every feed club,
  // so they only diverge for sites configured before the broadcast existed).
  readonly feedConfig = computed<ClubsiteFeedConfig | null>(() => {
    const configs = this.feedConfigs();
    for (const id of this.feedClubIds()) {
      if (configs[id]) return configs[id];
    }
    return null;
  });

  // Projection completion stamps per club (server-written at the end of a
  // rebuild), for the "preparing results" state after a config change.
  readonly feedMetas = toSignal(
    toObservable(computed(() => this.feedClubIds().join(","))).pipe(
      switchMap((joined) => {
        const ids = joined ? joined.split(",") : [];
        if (!ids.length) return of({} as Record<string, { projectedAt?: number; siteUrl?: string | null } | null>);
        return combineLatest(
          ids.map((id) =>
            runInInjectionContext(this.injector, () =>
              objectVal<{ projectedAt?: number; siteUrl?: string | null }>(
                ref(this.mdb, `clubsitesPublic/${id}/meta`)
              )
            ).pipe(map((meta) => [id, meta ?? null] as const))
          )
        ).pipe(map((pairs) => Object.fromEntries(pairs)));
      })
    ),
    { initialValue: {} as Record<string, { projectedAt?: number; siteUrl?: string | null } | null> }
  );

  /** The active club's live website (server-maintained on the club's public
   *  meta, see functions/clubsites.js syncSiteUrl); null when the club has
   *  no published site. Readable by every member, unlike the site record. */
  readonly liveSiteUrl = computed<string | null>(() => {
    const clubID = this.clubServ.selectedClubId();
    if (!clubID) return null;
    return this.feedMetas()[clubID as string]?.siteUrl || null;
  });

  /** True while any enabled club's results are still being projected. */
  readonly projectionPending = computed<boolean>(() => {
    const configs = this.feedConfigs();
    const metas = this.feedMetas();
    return this.feedClubIds().some((id) => {
      const cfg = configs[id];
      if (!cfg?.enabled || !cfg.updatedAt) return false;
      const at = metas[id]?.projectedAt ?? 0;
      return at < cfg.updatedAt;
    });
  });


  // Broadcast to every club the card controls (all clubs on the site), so
  // enabling the feed once covers a multi-venue site. `clubIdsOverride`
  // covers the just-created-site window before ownedSites re-emits.
  saveFeedConfig(config: ClubsiteFeedConfig, clubIdsOverride?: string[]) {
    const clubIds = clubIdsOverride?.length
      ? clubIdsOverride
      : this.feedClubIds();
    if (!clubIds.length) return Promise.reject(new Error("no club"));
    const stamped = { ...config, updatedAt: Date.now() };
    return Promise.all(
      clubIds.map((clubID) =>
        runInInjectionContext(this.injector, () =>
          set(ref(this.mdb, `clubsitesPublic/${clubID}/config`), stamped)
        )
      )
    );
  }

  // Starter site configs, hand-authored by Blind Valet (script-seeded under
  // /clubsiteStarters; public read). A starter is a site-shaped config
  // (theme/ticker/pages, no identity or billing) + display meta.
  async getStarters(): Promise<
    { key: string; name: string; order: number; config: any }[]
  > {
    const snap = await runInInjectionContext(this.injector, () =>
      get(ref(this.mdb, "clubsiteStarters"))
    );
    const val = snap.val() || {};
    return Object.entries(val)
      .map(([key, s]: [string, any]) => ({
        key,
        name: s?.meta?.name || key,
        order: s?.meta?.order ?? 0,
        config: s?.config || {},
      }))
      .sort((a, b) => a.order - b.order);
  }

  async isSlugAvailable(slug: string, ownSiteId?: string): Promise<boolean> {
    if (!slug || !isClaimableSlug(slug)) return false;
    const snap = await runInInjectionContext(this.injector, () =>
      get(ref(this.mdb, `clubsiteSlugs/${slug}`))
    );
    return !snap.exists() || snap.val() === ownSiteId;
  }

  // Site node first, slug index second (rules validate the slug against the
  // already-written site). If the claim loses a race, roll the site back.
  /** A fresh site id, so uploads can land under clubsites/{id}/ before the
   *  site is created. */
  newSiteId(): string {
    return runInInjectionContext(
      this.injector,
      () => push(ref(this.mdb, "clubsites")).key
    ) as string;
  }

  async createSite(entity: Omit<ClubSiteEntity, "id">, siteId = this.newSiteId()): Promise<string> {
    await runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, `clubsites/${siteId}`), entity)
    );
    try {
      await runInInjectionContext(this.injector, () =>
        set(ref(this.mdb, `clubsiteSlugs/${entity.slug}`), siteId)
      );
    } catch (err) {
      await runInInjectionContext(this.injector, () =>
        remove(ref(this.mdb, `clubsites/${siteId}`))
      );
      throw err;
    }
    return siteId;
  }

  // Entitled while Stripe considers the sub collectible (past_due keeps the
  // site up during dunning; deletion at the end of retries unpublishes
  // server-side) or when the site is comped (pilot clubs, server-set).
  // KEEP IN SYNC with siteBillingActive in functions/clubsites.js.
  static billingActive(billing?: ClubsiteBilling | null): boolean {
    if (!billing) return false;
    if (billing.comp) {
      // Comps carry their end date in currentPeriodEnd (0 = open-ended).
      return !billing.currentPeriodEnd || billing.currentPeriodEnd > Date.now();
    }
    return (
      billing.status === "active" ||
      billing.status === "trialing" ||
      billing.status === "past_due"
    );
  }

  siteBillingActive(): boolean {
    return ClubsiteService.billingActive(this.site()?.billing);
  }

  subscribeSite(siteId: string, pm?: string): Promise<any> {
    const f = this.fbFunc.httpsCallable("subscribeClubsite");
    return f(pm ? { siteId, pm } : { siteId }).then((res: any) => res.data ?? res);
  }

  cancelSiteSubscription(siteId: string, cancel: boolean): Promise<any> {
    const f = this.fbFunc.httpsCallable("cancelClubsiteSubscription");
    return f({ siteId, cancel }).then((res: any) => res.data ?? res);
  }

  // After SCA confirmation the webhook writes billing asynchronously — wait
  // for the site listener to reflect an entitled state (bounded).
  waitForBillingActive(maxMs = 20000): Promise<boolean> {
    return firstValueFrom(
      interval(500).pipe(
        filter(() => this.siteBillingActive()),
        take(1),
        timeout(maxMs)
      )
    )
      .then(() => true)
      .catch(() => false);
  }

  // --- Articles (Editor+ any; Contributor own + unpublished, per rules) ---

  saveArticle(siteId: string, article: SiteArticle): Promise<string> {
    const { id, ...data } = article;
    if (id) {
      return runInInjectionContext(this.injector, () =>
        set(ref(this.mdb, `clubsites/${siteId}/articles/${id}`), data)
      ).then(() => id);
    }
    const key = runInInjectionContext(
      this.injector,
      () => push(ref(this.mdb, `clubsites/${siteId}/articles`)).key
    ) as string;
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, `clubsites/${siteId}/articles/${key}`), data)
    ).then(() => key);
  }

  deleteArticle(siteId: string, articleId: string) {
    return runInInjectionContext(this.injector, () =>
      remove(ref(this.mdb, `clubsites/${siteId}/articles/${articleId}`))
    );
  }

  // --- Albums (same rules as articles) ---

  // Key for a new album, so its photos can upload under it before the
  // first save.
  newAlbumId(siteId: string): string {
    return runInInjectionContext(
      this.injector,
      () => push(ref(this.mdb, `clubsites/${siteId}/albums`)).key
    ) as string;
  }

  saveAlbum(siteId: string, album: SiteAlbum & { id: string }): Promise<string> {
    const { id, ...data } = album;
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, `clubsites/${siteId}/albums/${id}`), data)
    ).then(() => id);
  }

  deleteAlbum(siteId: string, albumId: string) {
    return runInInjectionContext(this.injector, () =>
      remove(ref(this.mdb, `clubsites/${siteId}/albums/${albumId}`))
    );
  }

  // Targeted page-tree save for the builder (avoids rewriting the whole
  // entity on every drag/reorder round-trip).
  savePages(siteId: string, pages: Record<string, SitePage>) {
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, `clubsites/${siteId}/pages`), pages)
    );
  }

  // --- Drafts (versioned design) ---

  draftsOf(site: ClubSiteEntity | null | undefined): SiteDraftRow[] {
    const map = site?.drafts || {};
    return Object.keys(map)
      .map((id) => ({ ...map[id], id }))
      .sort(
        (a, b) =>
          Number(!!a.auto) - Number(!!b.auto) ||
          (b.updatedAt || 0) - (a.updatedAt || 0)
      );
  }

  async createDraft(
    site: ClubSiteEntity,
    name: string,
    design: SiteDesign,
    basedOn: string
  ): Promise<string> {
    const id = runInInjectionContext(
      this.injector,
      () => push(ref(this.mdb, `clubsites/${site.id}/drafts`)).key
    ) as string;
    const now = Date.now();
    const draft: SiteDraft = { name, createdAt: now, updatedAt: now, basedOn, design };
    await runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, `clubsites/${site.id}/drafts/${id}`), draft)
    );
    return id;
  }

  renameDraft(site: ClubSiteEntity, draftId: string, name: string) {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, `clubsites/${site.id}/drafts/${draftId}`), { name })
    );
  }

  deleteDraft(site: ClubSiteEntity, draftId: string) {
    return runInInjectionContext(this.injector, () =>
      remove(ref(this.mdb, `clubsites/${site.id}/drafts/${draftId}`))
    );
  }

  // --- Team (website roles) ---

  teamOf(site: ClubSiteEntity | null | undefined): SiteTeamRow[] {
    const map = site?.team || {};
    return Object.keys(map)
      .map((uid) => ({ ...map[uid], uid }))
      .sort(
        (a, b) =>
          (a.memberName || "").localeCompare(b.memberName || "") ||
          a.addedAt - b.addedAt
      );
  }

  // One-shot member lists of the site's clubs the viewer belongs to (rules
  // only let members read a club); grouped per club for the Team picker.
  // A snapshot, not a listener: the picker is a rare admin action.
  async teamCandidateGroups(
    site: ClubSiteEntity,
    viewerClubs: { clubID: string; clubName: string }[]
  ): Promise<{ clubId: string; clubName: string; members: any[] }[]> {
    const readable = viewerClubs.filter((c) => site.clubIds && site.clubIds[c.clubID]);
    const groups = await Promise.all(
      readable.map(async (c) => {
        const snap = await runInInjectionContext(this.injector, () =>
          get(ref(this.mdb, `clubs/${c.clubID}/members`))
        ).catch(() => null);
        const map = (snap && snap.val()) || {};
        return { clubId: c.clubID, clubName: c.clubName, members: Object.values(map) };
      })
    );
    return groups.sort((a, b) => a.clubName.localeCompare(b.clubName));
  }

  // role null = remove. Server validates the caller (MANAGE_TEAM) and that
  // the member belongs to one of the site's clubs, then writes the team
  // entry and the profile-side discovery index together.
  setTeamRole(siteId: string, uid: string, role: SiteRole | null): Promise<any> {
    const f = this.fbFunc.httpsCallable("setClubsiteTeamRole");
    return f({ siteId, uid, role }).then((res: any) => res.data ?? res);
  }

  // --- Feature flag: Website product visible globally, per club, or for every
  // club a given owner runs (pilot operators own many clubs) ---
  // /config/features/clubsites = { enabled?: boolean, clubs?: {clubID: true},
  //                                owners?: {ownerUid: true} }
  // (public-read config node, written by blindvalet-scripts/set-clubsite-feature.js).
  readonly featureFlag = toSignal(
    runInInjectionContext(this.injector, () =>
      objectVal<{
        enabled?: boolean;
        clubs?: Record<string, true>;
        owners?: Record<string, true>;
      } | null>(ref(this.mdb, "config/features/clubsites"))
    ),
    { initialValue: undefined }
  );
  /** True once the flag has loaded and the active club is allowed (by club
   *  id or by its owner). Staff of an allowed club see the feature too. */
  readonly featureEnabled = computed(() => {
    const flag = this.featureFlag();
    if (!flag) return false;
    if (flag.enabled === true) return true;
    const clubID = this.clubServ.selectedClubId();
    if (clubID && flag.clubs && flag.clubs[clubID]) return true;
    const owner = this.clubServ.club()?.owner;
    return !!(owner && flag.owners && flag.owners[owner]);
  });

  // --- Bring your own domain ---

  readonly domainTargets = toSignal(
    runInInjectionContext(this.injector, () =>
      objectVal<{ cnameTarget?: string; apexIP?: string; registrarUrl?: string } | null>(
        ref(this.mdb, "config/clubsites")
      )
    ),
    { initialValue: null }
  );

  setDomainRequest(site: ClubSiteEntity, request: ClubsiteDomainRequest | null) {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, `clubsites/${site.id}`), { customDomainRequest: request })
    );
  }

  checkDomain(siteId: string): Promise<any> {
    const f = this.fbFunc.httpsCallable("checkClubsiteDomain");
    return f({ siteId }).then((res: any) => res.data ?? res);
  }
  activateDomain(siteId: string): Promise<any> {
    const f = this.fbFunc.httpsCallable("activateClubsiteDomain");
    return f({ siteId }).then((res: any) => res.data ?? res);
  }
  // Server-side: the rules can't let a client delete the site node, and
  // the slug, public club data and site photos go with it.
  deleteSite(siteId: string): Promise<string> {
    const f = this.fbFunc.httpsCallable("deleteClubsite");
    return f({ siteId }).then((res: any) => res.data ?? res);
  }
  removeDomain(siteId: string): Promise<any> {
    const f = this.fbFunc.httpsCallable("removeClubsiteDomain");
    return f({ siteId }).then((res: any) => res.data ?? res);
  }

  // Autosave target: the draft only. No root key is ever written here.
  saveDraft(site: ClubSiteEntity, draftId: string, design: SiteDesign) {
    return runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, `clubsites/${site.id}`), {
        [`drafts/${draftId}/design`]: design,
        [`drafts/${draftId}/updatedAt`]: Date.now(),
      })
    );
  }

  // Promote a draft: copy every key it carries onto the root (each key
  // replaced whole, so removed pages don't linger), record provenance, keep
  // the outgoing live values as the single "Previous live" backup, then
  // claim/release the address when the slug changed. Throws "SLUG_TAKEN"
  // before writing anything when the draft's address is not available.
  async publishDraft(site: ClubSiteEntity, draftId: string, backupName: string) {
    const draft = site.drafts?.[draftId];
    if (!draft) throw new Error("draft not found");
    const design: any = draft.design || {};
    const newSlug: string | null =
      typeof design.slug === "string" && design.slug && design.slug !== site.slug
        ? design.slug
        : null;
    if (newSlug && !(await this.isSlugAvailable(newSlug, site.id))) {
      throw new Error("SLUG_TAKEN");
    }
    const now = Date.now();
    const data: any = {};
    for (const key of DRAFT_KEYS) {
      if (!(key in design)) continue;
      const value = design[key] ?? null;
      // Identity keys never go null on the live record.
      if (value === null && (key === "name" || key === "slug" || key === "clubIds" || key === "language")) continue;
      // clubIds is owner-only in rules (club ownership is validated per
      // club); an unchanged set is left out so team publishes pass.
      if (key === "clubIds" && sameKeys(value, site.clubIds)) continue;
      data[key] = value;
    }
    data.live = { publishedAt: now, fromDraftId: draftId, fromDraftName: draft.name || "" };
    data[`drafts/${PREVIOUS_LIVE_ID}`] = {
      name: backupName,
      auto: true,
      createdAt: now,
      updatedAt: now,
      basedOn: "live",
      design: designOf(site),
    } as SiteDraft;
    purgeLegacyKeys(site, data);
    await runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, `clubsites/${site.id}`), data)
    );
    if (newSlug) {
      await runInInjectionContext(this.injector, () =>
        set(ref(this.mdb, `clubsiteSlugs/${newSlug}`), site.id)
      );
      await runInInjectionContext(this.injector, () =>
        remove(ref(this.mdb, `clubsiteSlugs/${site.slug}`))
      );
    }
  }

  // Preview of a draft (or live when draftId is null): the local renderer in
  // dev, the public address in prod. Drafts are viewable by anyone with the
  // link (push-key id, noindex, never cached).
  previewUrl(slug: string, draftId: string | null): string {
    const base = (environment as any).sitesPreviewBase;
    const draft = draftId ? `draft=${encodeURIComponent(draftId)}` : "";
    if (base) return `${base}/?site=${slug}${draft ? "&" + draft : ""}`;
    return `${hostedSiteUrl(slug)}/${draft ? "?" + draft : ""}`;
  }

  // Targeted merge: only the patched keys are written (never the whole
  // entity — ownerUid/billing/team are owner- or server-only in rules, and a
  // team member's multi-path update fails as a whole if any key is denied).
  async updateSite(
    site: ClubSiteEntity,
    patch: Partial<ClubSiteEntity>,
    extraPaths: Record<string, any> = {}
  ) {
    // billing and team are server-written (rules reject them); articles and
    // albums are saved through their own paths; drafts and live provenance
    // have their own writers.
    const { id, billing, team, articles, albums, drafts, live, ...data } = {
      ...patch,
      ...extraPaths,
    } as any;
    purgeLegacyKeys(site, data);
    const oldSlug = site.slug;
    const newSlug = data.slug;
    await runInInjectionContext(this.injector, () =>
      update(ref(this.mdb, `clubsites/${site.id}`), data)
    );
    if (newSlug && newSlug !== oldSlug) {
      await runInInjectionContext(this.injector, () =>
        set(ref(this.mdb, `clubsiteSlugs/${newSlug}`), site.id)
      );
      await runInInjectionContext(this.injector, () =>
        remove(ref(this.mdb, `clubsiteSlugs/${oldSlug}`))
      );
    }
  }
}
